<template>
    <div>
        <page-header
            ref="pageHeader"
            :text="$t('navigation.certificates')"
            :searchField="{
                placeholder: $t('certificates.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                defaultField: 'date',
                savedSearchName: 'search-certificates',
            }"
            @search="searchHandler"
        />

        <div class="table-wrapper">
            <table
                class="table table-filter table--striped table--filterable"
                v-show="items.length > 0"
            >
                <thead>
                    <tr>
                        <th
                            v-for="field in fields"
                            :key="field.value"
                            class="table-filter__trigger"
                            :width="field.width"
                        >
                            <div>{{ $t(field.title) }}</div>
                            <div
                                v-if="field.sortable"
                                class="table-filter__sorting"
                                @click="sortClickHandler(field.value)"
                                :class="{
                                    asc: activeSorting[field.value] === 'asc',
                                    desc: activeSorting[field.value] === 'desc',
                                }"
                            >
                                <i class="icon icon--common-caret"></i>
                            </div>
                        </th>
                        <th width="8%"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="certificate in filteredCertificates" :key="certificate.id">
                        <td>{{ certificate.mac_address }}</td>
                        <td>{{ certificate.serial_number }}</td>
                        <td>{{ certificate.request_type }}</td>
                        <td>{{ certificate.device_type }}</td>
                        <td>{{ certificate.created_at }}</td>
                        <td class="nowrap">
                            <badge :type="determineStatusClass(certificate.status)">{{
                                $t(`certificates.status_modes.${certificate.status}`)
                            }}</badge>

                            <a
                                v-if="certificate.status === 'requested'"
                                @click="reloadCertificate(certificate)"
                                class="link link--gray link--spinnable slab-mobile--small--left"
                                :class="{ spin: loadingElements.indexOf(certificate.id) >= 0 }"
                            >
                                <i class="icon icon--action-sync"></i>
                            </a>
                        </td>
                        <td>
                            <a
                                class="button button--attention"
                                @click.prevent="viewCertificate(certificate, true)"
                                :class="{ 'ng-hide': certificate.loading }"
                                :title="$t('certificates.show_certificate')"
                            >
                                <i class="icon icon--2x icon--action-view"></i>
                            </a>
                            <a
                                class="button button--attention"
                                @click.prevent="deleteCertificate(certificate)"
                                :class="{ 'ng-hide': certificate.loading }"
                                :title="$t('certificates.delete_certificate')"
                            >
                                <i class="icon icon--2x icon--action-decline"></i>
                            </a>
                            <i
                                class="icon icon--common-spinner spinner"
                                :class="{ 'spinner--finished': !certificate.loading }"
                            ></i>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="paginationData"
                    :paginationData="paginationData"
                    :visibleAmount="searchedItems ? searchedItems.length : items.length"
                    @goToPrevPage="prevPageHandler"
                    @goToNextPage="nextPageHandler"
                />
            </table>
        </div>

        <div class="alert alert--warning" role="alert" v-if="loaded && items.length === 0">
            {{ $t('certificates.no_certificates_found') }}
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import TablePagination from '@/components/TablePagination';
import Badge from '@/components/Badge';
import paginatedTable from '@/mixins/paginatedTable';
import * as moment from 'moment';

export default {
    components: {
        PageHeader,
        TablePagination,
        Badge,
    },
    mixins: [paginatedTable],
    data() {
        return {
            loadingElements: [],
            loaded: false,
            apiType: 'certificates',
            responseField: 'certificates',
            searchFields: ['date', 'mac', 'sn'],
            fields: [
                {
                    value: 'mac_address',
                    title: 'certificates.mac_address',
                    sortable: true,
                    width: '8%',
                },
                {
                    value: 'serial_number',
                    title: 'certificates.serial_number',
                    sortable: true,
                    width: '10%',
                },
                {
                    value: 'request_type',
                    title: 'certificates.request_type',
                    sortable: true,
                    width: '5%',
                },
                {
                    value: 'device_type',
                    title: 'certificates.device_type',
                    sortable: true,
                    width: '10%',
                },
                {
                    value: 'created_at',
                    title: 'certificates.created_at',
                    sortable: true,
                    width: '10%',
                },
                {
                    value: 'status',
                    title: 'certificates.status',
                    sortable: true,
                    width: '5%',
                },
            ],
        };
    },
    computed: {
        filteredCertificates() {
            if (!this.items) return [];

            let filtered = this.searchedItems ? [...this.searchedItems] : [...this.items];
            return this.prepareCertificates(filtered);
        },
    },
    created() {
        this.loadItems();
        this.showInitialSearch();
    },
    methods: {
        showInitialSearch() {
            // set search to date of two weeks ago per default
            const date = new Date();
            date.setDate(date.getDate() - 14);
            const dateString = moment(date).format('YYYY-MM-DD');
            this.activeSearchTerm = dateString;
            setTimeout(() => {
                this.$refs.pageHeader.setInitialSearchValue(dateString);
            }, 10);
        },
        prepareCertificates(certificates) {
            return certificates.map(cert => {
                const preparedCert = { ...cert };
                preparedCert.loading = false;
                preparedCert.reloading = false;
                preparedCert.created_at = moment(preparedCert.created_at).format(
                    'YYYY-MM-DD HH:mm:ss'
                );
                return preparedCert;
            });
        },
        determineStatusClass(status) {
            switch (status) {
            case 'requested':
                return 'error';
            case 'renew':
                return 'error';
            case 'issued':
                return 'success';
            case 'expired':
            case 'revoked':
                return 'alert';
            default:
                return 'blank';
            }
        },
        viewCertificate(certificate) {
            this.$modal.show(
                () => import('@/views/modal/ViewCertificate.vue'),
                {
                    certificate: certificate,
                },
                {
                    name: 'ViewCertificate',
                    scrollable: true,
                    width: 660,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        deleteCertificate(certificate) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('certificates.delete_certificate_text'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: () => {
                        certificate.loading = true;

                        api.certificates.delete({ certificateid: certificate.id }).then(() => {
                            certificate.loading = false;
                            if (this.activeSearchTerm) {
                                this.searchItems(this.activeSearchTerm);
                            } else {
                                this.loadItems();
                            }
                        });
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        reloadCertificate(certificate) {
            if (this.loadingElements.indexOf(certificate.id) < 0) {
                this.loadingElements.push(certificate.id);

                api.certificates
                    .retry({ certificateid: certificate.id })
                    .then(response => {
                        this.removeLoadingElement(certificate.id);
                        this.loadItems(this.activePage);

                        // if (response.status >= 200 && response.status < 300) {
                        //     certificate.status = 'issued';
                        // }
                    })
                    .catch(() => {
                        this.loadItems(this.activePage);
                        this.removeLoadingElement(certificate.id);
                    });
            }
        },
        removeLoadingElement(i) {
            var index = this.loadingElements.indexOf(i);
            if (index !== -1) {
                this.loadingElements.splice(index, 1);
            }
        },
    },
};
</script>
